import { DateFilterOptionsConst, DateFilterType, IParkingModel } from '@aggregator/core';

import { Component, DestroyRef, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  ITableConfig,
  UI_TABLE_CUSTOM_COLUMN,
  UIButton,
  UICard,
  UIFormDate,
  UIFormModule,
  UILoading,
} from '@shared/ui';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { BehaviorSubject, combineLatest } from 'rxjs';
import {
  DashboardCityMapChartComponent,
  DashboardGeneralInfoComponent,
  DashboardLineChartComponent,
  DashboardTypeInfoComponent,
} from './components';
import { operatorTableConfig, parkingTableConfig } from './configs';
import {
  DashboardGeneralStatisticInfoModel,
  DashboardGroupedStatisticInfoModel,
  DashboardOperatorLoadStatisticModel,
  DashboardParkingLoadStatisticModel,
  DashboardParkingStatisticModel,
} from './models';
import { DashboardService } from './services';

@Component({
  selector: 'aggregator-dashboard-page',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    UIFormModule,
    UIButton,
    UICard,
    UIFormDate,
    UILoading,
    DashboardLineChartComponent,
    DashboardGeneralInfoComponent,
    ReactiveFormsModule,
    NzSelectModule,
    DashboardTypeInfoComponent,
    DashboardCityMapChartComponent,
  ],
  providers: [
    DashboardService,
    {
      provide: UI_TABLE_CUSTOM_COLUMN,
      useValue: {
        'parking-load-column': {
          component: () => import('./components').then(m => m.DashboardParkingLoadComponent),
        },
        'parking-load-chart-column': {
          component: () => import('./components').then(m => m.DashboardParkingLoadChartComponent),
        },
        'parking-load-difference-column': {
          component: () =>
            import('./components').then(m => m.DashboardParkingLoadDifferenceComponent),
        },
      },
    },
  ],
})
export class DashboardPage implements OnInit {
  operatorTableConfig: ITableConfig = operatorTableConfig;
  operatorList: DashboardOperatorLoadStatisticModel[] = [];
  isOperatorListLoading = true;
  operatorListTotal = 0;
  operatorPageSize = 20;
  operatorPageIndex = 1;

  parkingTableConfig: ITableConfig = parkingTableConfig;
  parkingList: DashboardParkingLoadStatisticModel[] = [];
  isParkingListLoading = true;
  parkingListTotal = 0;
  parkingPageSize = 20;
  parkingPageIndex = 1;

  dateFilterOptions = DateFilterOptionsConst;
  DateFilterType = DateFilterType;

  generalData?: DashboardGeneralStatisticInfoModel[];

  parkingTypeData?: DashboardGroupedStatisticInfoModel;
  parkingCategoryData?: DashboardGroupedStatisticInfoModel;
  parkingTariffData?: DashboardGroupedStatisticInfoModel;
  parkingStatusData?: DashboardGroupedStatisticInfoModel;
  parkingOptionData?: DashboardGroupedStatisticInfoModel;

  parkingTransportData?: DashboardGroupedStatisticInfoModel;

  parkingStatisticData?: DashboardParkingStatisticModel;

  filterForm?: FormGroup;
  filterData$ = new BehaviorSubject<{ startDate: string; endDate: string } | null>(null);

  get filterValue(): { startDate: string; endDate: string } {
    const filter = this.filterForm?.value;
    const payload = {
      startDate: new Date(filter?.startDate).toISOString(),
      endDate: new Date(filter?.endDate).toISOString(),
    };

    return payload;
  }

  parkingSelectControl = new FormControl<IParkingModel | string | null>(null);
  parkingDateControl = new FormControl<string>(new Date().toISOString());

  isLoading = true;

  constructor(
    private dashboardService: DashboardService,
    private destroyRef: DestroyRef,
    private fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    const now = new Date();
    this.filterForm = this.fb.group({
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: new Date(),
    });

    combineLatest([
      this.dashboardService.getGeneralStatisticInfo(this.filterValue),
      this.dashboardService.getGroupedStatisticInfo(this.filterValue),
      this.dashboardService.getFirstParking(),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([generalResponse, groupedResponse, firstParkingResponse]) => {
        this.generalData = generalResponse?.value ?? [];

        const grouped = groupedResponse?.value ?? [];
        this.parkingTypeData = grouped[0];
        this.parkingTariffData = grouped[1];
        this.parkingCategoryData = grouped[2];
        this.parkingStatusData = grouped[3];
        this.parkingOptionData = grouped[4];

        this.parkingSelectControl.setValue(firstParkingResponse?.value?.items?.[0] ?? null);

        this.isLoading = false;
      });

    // Подписка на изменение периода парковок
    combineLatest([this.parkingSelectControl.valueChanges, this.parkingDateControl.valueChanges])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([parkingId, date]) => {
        this.loadParkingStats(parkingId as string, date);
      });
  }

  onApplyFilter(): void {
    this.loadGroupedStatistics();
  }

  loadGroupedStatistics(): void {
    this.dashboardService
      .getGroupedStatisticInfo(this.filterValue)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        const data = response?.value ?? [];
        this.parkingTypeData = data[0];
        this.parkingTariffData = data[1];
        this.parkingCategoryData = data[2];
        this.parkingStatusData = data[3];
        this.parkingOptionData = data[4];
      });
  }

  loadOperatorStats(period: number): void {
    this.isOperatorListLoading = true;

    this.dashboardService
      .getOperatorLoadStatistic({
        period,
        pageIndex: this.operatorPageIndex,
        pageSize: this.operatorPageSize,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.operatorList = response?.value?.items ?? [];
          this.operatorListTotal = response?.value?.total ?? [];
          this.isOperatorListLoading = false;
        },
        error: () => {
          this.isOperatorListLoading = false;
        },
      });
  }

  loadParkingStats(parkingId: string | null, date?: string | null): void {
    if (!parkingId) return;
    date = date ?? new Date().toISOString();
    this.isParkingListLoading = true;

    this.dashboardService
      .getParkingStatisticById(parkingId, date)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.isParkingListLoading = false;
          this.parkingStatisticData = response?.value ?? null;
        },
        error: () => {
          this.isParkingListLoading = false;
        },
      });
  }
}
